import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { X, Calendar, CheckCircle2 } from 'lucide-react';

const CrearReporte = ({ startups, onCrear, onCancelar }) => {
  const [formData, setFormData] = useState({
    trimestre: '',
    año: new Date().getFullYear(),
    startupsSeleccionadas: []
  });

  const trimestres = [
    { value: 'Q1', label: 'Q1 (Enero - Marzo)' },
    { value: 'Q2', label: 'Q2 (Abril - Junio)' },
    { value: 'Q3', label: 'Q3 (Julio - Septiembre)' },
    { value: 'Q4', label: 'Q4 (Octubre - Diciembre)' }
  ];

  const handleToggleStartup = (startupId) => {
    setFormData(prev => ({
      ...prev,
      startupsSeleccionadas: prev.startupsSeleccionadas.includes(startupId)
        ? prev.startupsSeleccionadas.filter(id => id !== startupId)
        : [...prev.startupsSeleccionadas, startupId]
    }));
  };

  const handleSeleccionarTodas = () => {
    if (formData.startupsSeleccionadas.length === startups.length) {
      setFormData(prev => ({ ...prev, startupsSeleccionadas: [] }));
    } else {
      setFormData(prev => ({ ...prev, startupsSeleccionadas: startups.map(s => s.id) }));
    }
  };

  const handleCrear = () => {
    if (!formData.trimestre) {
      alert('Por favor selecciona un trimestre');
      return;
    }

    if (formData.startupsSeleccionadas.length === 0) {
      alert('Por favor selecciona al menos una startup');
      return;
    }

    const nuevoReporte = {
      nombre: `${formData.trimestre} ${formData.año}`,
      trimestre: formData.trimestre,
      año: formData.año,
      fechaCreacion: new Date().toISOString().split('T')[0],
      estado: 'en-proceso',
      startupsIds: formData.startupsSeleccionadas,
      startupsCompletas: 0,
      startupsTotales: formData.startupsSeleccionadas.length
    };

    onCrear(nuevoReporte);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Crear Nuevo Reporte Trimestral</CardTitle>
            <CardDescription>
              Selecciona el trimestre y las startups a incluir en el reporte
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onCancelar}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Trimestre <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.trimestre}
                onChange={(e) => setFormData({ ...formData, trimestre: e.target.value })}
                className="w-full p-2 rounded-md border border-input bg-background"
              >
                <option value="">Selecciona un trimestre</option>
                {trimestres.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Año <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.año}
                onChange={(e) => setFormData({ ...formData, año: parseInt(e.target.value) })}
                className="w-full p-2 rounded-md border border-input bg-background"
                min="2020"
                max="2100"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">
                Startups Participadas <span className="text-red-500">*</span>
              </label>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSeleccionarTodas}
              >
                {formData.startupsSeleccionadas.length === startups.length 
                  ? 'Deseleccionar Todas' 
                  : 'Seleccionar Todas'}
              </Button>
            </div>
            
            <div className="border border-border/50 rounded-lg p-4 max-h-[300px] overflow-y-auto space-y-2">
              {startups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No hay startups registradas. Ve a "Gestión Participadas" para crear una.
                </p>
              ) : (
                startups.map((startup) => (
                  <label
                    key={startup.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/30 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.startupsSeleccionadas.includes(startup.id)}
                      onChange={() => handleToggleStartup(startup.id)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{startup.nombre}</p>
                      <p className="text-xs text-muted-foreground">{startup.fundador}</p>
                    </div>
                    {formData.startupsSeleccionadas.includes(startup.id) && (
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                    )}
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {formData.startupsSeleccionadas.length} startup(s) seleccionada(s)
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/50">
            <Button variant="outline" onClick={onCancelar}>
              Cancelar
            </Button>
            <Button onClick={handleCrear} disabled={startups.length === 0}>
              <Calendar className="h-4 w-4 mr-2" />
              Crear Reporte
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CrearReporte;

